/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Button } from '@atgfr8/atg-ui-tailwind';
import { ToastType } from '@components';
import {
  customerStore,
  orderStore,
  shareAndUnShareLoadCapacity,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { AccountGroupOutlineIcon } from '@atgfr8/icons';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { isEmpty } from 'lodash';
import { createSignal, Show } from 'solid-js';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

const ShareAndUnShareCapacity = (props: PropsT) => {
  const [capacityLoading, setCapacityLoading] = createSignal(false);
  const shareOrUnShareCapacity = async () => {
    try {
      setCapacityLoading(true);
      const payload = {
        id: props.load.id,
        customerId: customerStore.customer.id,
        loadId: orderStore.order.loads[props.tabIndex].id,
        orderId: orderStore.order.id,
        removeCapacity: props.load.hasBeenSharedWithCapacity,
      };
      const res = await shareAndUnShareLoadCapacity(payload);

      if (!isEmpty(res) && Boolean(res.isSuccess)) {
        const targetIndex =
          orderStore.order.id === payload.orderId
            ? orderStore.order.loads.findIndex((l) => l.id === payload.loadId)
            : -1;
        if (targetIndex >= 0) {
          const targetLoad = orderStore.order.loads[targetIndex];
          updateLoadPropertyAtIndex(
            {
              hasBeenSharedWithCapacity: !payload.removeCapacity,
              assignedToUserId: res.value.assignedId,
              assignedToUser: res.value.assignedName,
              capacityGroupName: res.value.capacityGroupName,
              maxPrice:
                targetLoad.maxPrice === undefined ||
                targetLoad.maxPrice === null
                  ? res.value.maxBuyRate
                  : targetLoad.maxPrice,
            },
            targetIndex,
          );
        }
      }
      handleToast(
        ToastType.Success,
        payload.removeCapacity ? 'Capacity Un-Shared' : 'Capacity Shared',
      );
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    } finally {
      setCapacityLoading(false);
    }
  };

  const shareCapacityDisabled = () => {
    return (
      !(customerStore.customer.capacityGroupId !== null) ||
      props.load.id < 1 ||
      capacityLoading() ||
      ['Billed', 'Void', 'Paid'].includes(props.load.status ?? '') ||
      props.load.hideCustomerName
    );
  };

  const shareUnShareButtonLabel = () =>
    props.load.hasBeenSharedWithCapacity
      ? 'Un-Share Capacity'
      : 'Share Capacity';

  const shareUnShareButtonColor = () =>
    props.load.hasBeenSharedWithCapacity || shareCapacityDisabled()
      ? '#0000001f'
      : '#248670';

  const shareUnShareButtonTitle = () => {
    if (
      ['Billed', 'Void', 'Paid'].includes(props.load.status ?? '') &&
      props.load.hasBeenSharedWithCapacity
    ) {
      return 'Cannot Un-Share after billing';
    }

    if (
      customerStore.customer.capacityGroupId === null &&
      !props.load.hasBeenSharedWithCapacity
    ) {
      return 'Please send an email to shared.capacity@armstrongtransport.com to inquire about Shared Capacity';
    }
    if (props.load.hasBeenSharedWithCapacity) {
      return 'Press to Un-Share';
    }
    return 'Share Capacity';
  };

  return (
    <div class="min-w-[170px]" title={shareUnShareButtonTitle()}>
      <Button
        size="sm"
        startIcon={<AccountGroupOutlineIcon size={20} />}
        disabled={shareCapacityDisabled()}
        class={`text-armstrong-sm! bg-[${shareUnShareButtonColor()}]!`}
        variant="contained"
        onClick={shareOrUnShareCapacity}
        dataTestId={formatDataTestId(shareUnShareButtonLabel(), 'button')}
      >
        <Show when={capacityLoading()}>
          <span class="loading loading-spinner loading-sm" />
        </Show>
        {shareUnShareButtonLabel()}
      </Button>
    </div>
  );
};

export default ShareAndUnShareCapacity;
